import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const C = {
  navy: '#1e3a5f', bg: '#f4f6f9', white: 'white',
  border: '#e8ecf0', text: '#0f172a', textMd: '#374151',
  textSm: '#6b7280', textXs: '#9ca3af',
  green: '#16a34a', greenBg: '#f0fdf4',
  red: '#dc2626', redBg: '#fef2f2',
  amber: '#d97706', amberBg: '#fffbeb',
};

const Acknowledge = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [delivery,     setDelivery]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [confirming,   setConfirming]   = useState(false);
  const [confirmed,    setConfirmed]    = useState(false);
  const [alreadyDone,  setAlreadyDone]  = useState(false);
  const [checked,      setChecked]      = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid link. Please check your email and try again.');
      setLoading(false);
      return;
    }

    axios.get(`${API_URL}/communications/verify/${token}`)
      .then(res => {
        setDelivery(res.data);
        if (res.data.acknowledged_at) setAlreadyDone(true);
      })
      .catch(() => setError('This link is invalid or has expired. Please contact your advisor.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    if (!checked) return;
    setConfirming(true);
    try {
      await axios.post(`${API_URL}/communications/acknowledge`, { token });
      setConfirmed(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      padding: '20px',
    }}>
      <div style={{
        background: C.white, borderRadius: 16, padding: '40px 36px',
        width: '100%', maxWidth: 480,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 52, height: 52, borderRadius: 14,
            background: C.navy, marginBottom: 14,
          }}>
            <span style={{ fontSize: 24 }}>🛡️</span>
          </div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>
            RegShield
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textSm }}>
            Luxembourg RegTech Platform
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <p style={{ textAlign: 'center', color: C.textSm, fontSize: 14 }}>
            Loading your documents…
          </p>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: C.redBg, borderRadius: 10, padding: '16px 20px', textAlign: 'center' }}>
            <p style={{ fontSize: 24, margin: '0 0 8px' }}>⚠️</p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.red }}>{error}</p>
          </div>
        )}

        {/* Already acknowledged */}
        {!loading && !error && alreadyDone && (
          <div style={{ background: C.greenBg, border: `1px solid #bbf7d0`, borderRadius: 10, padding: '20px', textAlign: 'center' }}>
            <p style={{ fontSize: 32, margin: '0 0 10px' }}>✅</p>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.green }}>
              Already confirmed
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: C.textSm }}>
              You already acknowledged these documents on{' '}
              {new Date(delivery.acknowledged_at).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}.
            </p>
          </div>
        )}

        {/* Confirmed just now */}
        {confirmed && (
          <div style={{ background: C.greenBg, border: `1px solid #bbf7d0`, borderRadius: 10, padding: '24px', textAlign: 'center' }}>
            <p style={{ fontSize: 40, margin: '0 0 12px' }}>✅</p>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.green }}>
              Thank you — documents confirmed
            </p>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: C.textSm, lineHeight: 1.6 }}>
              Your acknowledgement has been recorded in our compliance system.
              You may now close this window.
            </p>
          </div>
        )}

        {/* Main content */}
        {!loading && !error && !alreadyDone && !confirmed && delivery && (
          <>
            <div style={{
              background: '#f8fafc', borderRadius: 10, padding: '16px 18px',
              marginBottom: 20, border: `1px solid ${C.border}`,
            }}>
              <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: C.textXs, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Investment
              </p>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>
                {delivery.fund_name}
              </p>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textSm }}>
                {delivery.fund_manager_name}
              </p>
            </div>

            <p style={{ margin: '0 0 16px', fontSize: 14, color: C.textMd, lineHeight: 1.6 }}>
              Before proceeding with your investment, you must confirm that you have
              received and read the following documents:
            </p>

            {/* Document list */}
            <div style={{ marginBottom: 20 }}>
              {[
                {
                  icon: '📄',
                  title: 'Key Information Document (KID)',
                  desc: 'Describes the fund, its investment objectives, risks, costs and past performance.',
                },
                ...(delivery.suitability_assessment_id ? [{
                  icon: '📋',
                  title: 'Suitability Report',
                  desc: 'Confirms that this investment was assessed as suitable for your profile under MiFID II.',
                }] : []),
              ].map((doc, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 14, padding: '14px 16px',
                  background: C.white, border: `1px solid ${C.border}`,
                  borderRadius: 10, marginBottom: 8,
                }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{doc.icon}</span>
                  <div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>{doc.title}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: C.textSm, lineHeight: 1.5 }}>{doc.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Checkbox */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              cursor: 'pointer', marginBottom: 20, padding: '14px 16px',
              background: checked ? C.greenBg : '#fafbfc',
              border: `1px solid ${checked ? '#bbf7d0' : C.border}`,
              borderRadius: 10, transition: 'all 0.15s',
            }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={e => setChecked(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: C.textMd, lineHeight: 1.5 }}>
                I confirm that I have received, read and understood the documents listed above,
                including the Key Information Document and Suitability Report.
              </span>
            </label>

            {/* Legal note */}
            <div style={{
              background: C.amberBg, borderRadius: 8, padding: '10px 14px',
              marginBottom: 20,
            }}>
              <p style={{ margin: 0, fontSize: 11, color: '#92400e', lineHeight: 1.5 }}>
                This confirmation is required under MiFID II Article 25(6) and will be
                permanently recorded with a timestamp in our compliance system.
              </p>
            </div>

            <button
              onClick={handleConfirm}
              disabled={!checked || confirming}
              style={{
                width: '100%', padding: '14px',
                background: !checked ? '#e5e7eb' : confirming ? '#93c5fd' : C.navy,
                color: !checked ? C.textXs : 'white',
                border: 'none', borderRadius: 10,
                fontWeight: 700, fontSize: 14,
                cursor: !checked || confirming ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {confirming ? '⏳ Recording confirmation…' : 'Confirm I have read the documents'}
            </button>

            <p style={{ textAlign: 'center', fontSize: 11, color: C.textXs, marginTop: 14, marginBottom: 0 }}>
              This link expires on {new Date(delivery.token_expires_at).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Acknowledge;